// @ts-nocheck
import CircularProgress from '@mui/material/CircularProgress';
import AIGenerateButton from '../../../AIGenerateButton/AIGenerateButton';
import Editor from '../../../Editor/Editor';
import UnsavedChangesModal from '../../../Trip/Itinerary/UnsavedChangesModal/UnsavedChangesModal';
import ImagePicker from '../../../Upload/ImagePicker/ImagePicker';
import globalConfig from '../../../../config/globalConfig';
import {
  Box,
  Typography,
  Drawer,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { XGButton, XGInput } from 'xg-cl';
import { X, CaretDown, Plus, Trash } from '@phosphor-icons/react';
import { t } from 'i18next';
import { useEffect, useState, useRef } from 'react';
import { fileType, pathReference } from '../../../../constants/files';
import { getOldCoverImageUrl } from '../../../../utils/getCoverImageUrl';
import {
  saveContentToLibrary,
  updateContent
} from '../../../../services/library/content';
import { ErrorToast, SuccessToast } from '../../../../utils/alerts';
import { bytesToMiB } from '../../../../utils/fileHelper';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as PropTypes from 'prop-types';
import './LibraryItemSlideOut.css';

/**
 * LibraryItemSlideOut is a component that displays a single content item in a slide-out drawer.
 * It includes fields for the item's title, description, final description, internal notes, and images.
 * It also includes buttons to save changes to the item, cancel the edit, and delete the item.
 * Additionally, there is a button to create a final version of the item (if the item is a trip day).
 *
 * @param {Object} props - Properties passed to the component from its parent.
 * @param {Object} openSlide - Function to open the slide-out drawer.
 * @param {Object} handleSlideClose - Function to close the slide-out drawer.
 * @param {Object} libraryContentTypes - Array of content types available in the library.
 * @param {Object} handleOnItemAdded - Function to call when the item is added to the library.
 * @param {Object} initialContent - Object containing the initial content of the item.
 * @param {number} idSelected - ID of the selected item.
 * @param {Object} contentTypeSelected - Object containing information about the content type of the selected item.
 * @param {boolean} isEditing - Boolean indicating if the item is currently being edited.
 * @param {Object} handleDelete - Function to call when the item is deleted.
 */
const LibraryItemSlideOut = ({
  openSlide,
  handleSlideClose,
  libraryContentTypes,
  handleOnItemAdded,
  initialContent,
  idSelected,
  handleDelete,
  contentTypeSelected,
  isEditing,
  ...rest
}) => {
  const { enableStockPhotoPicker = false } = useFlags();
  const shouldTrackChangesRef = useRef(false);
  const isDay = contentTypeSelected?.code === 'day';
  const [content, setContent] = useState({
    contentid: initialContent.contentid
  });
  const [shouldShowDayElements, setShouldShowDayElements] = useState(false);
  const [editorDescription, setEditorDescription] = useState('');
  const [editorFinalDescription, setEditorFinalDescription] = useState('');
  const [editorNote, setEditorNote] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [description, setDescription] = useState('');
  const [finalDescription, setFinalDescription] = useState('');

  // Accordion expansion state (track which sections are expanded)
  const [expandedSections, setExpandedSections] = useState({
    description: true,
    finalDescription: true,
    internalNotes: true
  });
  const [showCreateFinalVersion, setShowCreateFinalVersion] = useState(
    !initialContent?.finalDescription
  );
  const [isEditorGenerating, setIsEditorGenerating] = useState(false);
  const [isFinalEditorGenerating, setIsFinalEditorGenerating] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [initialValues, setInitialValues] = useState({
    title: '',
    description: '',
    finalDescription: '',
    internalNote: '',
    imageUrl: ''
  });

  const normalizeValue = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setContent((prevState) => ({
      ...prevState,
      [name]: value
    }));
  };

  const setMarkdown = (value) => {
    setEditorDescription(value);
  };

  const setFinalDescriptionMarkdown = (value) => {
    setEditorFinalDescription(value);
  };

  const setNoteMarkdown = (value) => {
    setEditorNote(value);
  };

  const insertImage = async (newImageUrl) => {
    setContent((prevState) => ({
      ...prevState,
      images: [{ imageurl: newImageUrl, seq: 1 }]
    }));
  };

  const handleDeleteLibraryImage = async () => {
    setContent((prevState) => ({
      ...prevState,
      images: [],
      contentimage: []
    }));

    return { data: true };
  };

  const getCurrentImageUrl = () => {
    if (
      content?.images &&
      Array.isArray(content.images) &&
      content.images.length > 0
    ) {
      return getOldCoverImageUrl(
        content.images[content.images.length - 1].imageurl
      );
    }

    if (
      content?.contentimage &&
      Array.isArray(content.contentimage) &&
      content.contentimage.length > 0
    ) {
      return getOldCoverImageUrl(
        content.contentimage[content.contentimage.length - 1].imageurl
      );
    }

    return null;
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedModal(true);
    } else {
      resetAndClose();
    }
  };

  const resetAndClose = () => {
    setContent({
      contenttypeid: 0,
      title: '',
      description: '',
      note: '',
      images: []
    });
    setMarkdown('');
    setEditorDescription('');
    setEditorFinalDescription('');
    setEditorNote('');
    setHasUnsavedChanges(false);
    setShowUnsavedModal(false);
    handleSlideClose();
  };

  const isFormValid = () => {
    if (content.contenttypeid === 0) return false;
    if (content.title.length === 0) return false;
    return true;
  };

  const handleSave = async () => {
    if (!isFormValid()) {
      ErrorToast(t('library:errorSavingContent'));
      return;
    }

    try {
      setIsSaving(true);
      let result;
      idSelected > 0
        ? (result = await updateContent(content.contentid, {
            ...content,
            description: editorDescription,
            finaldescription: editorFinalDescription || null,
            note: shouldShowDayElements ? editorNote : null
          }))
        : (result = await saveContentToLibrary({
            ...content,
            description: editorDescription,
            finaldescription: editorFinalDescription || null,
            note: shouldShowDayElements ? editorNote : null
          }));

      if (result.error || !result.data) {
        ErrorToast(t('library:errorSavingContent'));
      } else {
        const contentimage = result.data.images;
        const newResult = { ...result.data, contentimage };

        if (idSelected > 0) {
          const arrayCopy = [...initialContent].map((el) => {
            if (el.contentid === newResult.contentid) {
              return { ...el, ...newResult };
            } else {
              return el;
            }
          });

          handleOnItemAdded(arrayCopy);
        } else {
          handleOnItemAdded([newResult, ...initialContent]);
        }

        SuccessToast(t('library:itemSaved'));
        setHasUnsavedChanges(false);
        setShowUnsavedModal(false);
        resetAndClose();
      }
    } catch (err) {
      ErrorToast(t('library:errorSavingContent'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteButton = async () => {
    handleDelete(content);
  };

  const handleUnsavedModalAction = (action) => {
    switch (action) {
      case 'continue':
        // Keep editing, close modal
        setShowUnsavedModal(false);
        break;
      case 'discard':
        // Reset to initial values and close
        setContent({
          ...content,
          title: initialValues.title
        });
        setMarkdown(initialValues.description);
        setEditorDescription(initialValues.description);
        setDescription(initialValues.description);
        setEditorFinalDescription(initialValues.finalDescription);
        setFinalDescription(initialValues.finalDescription);
        setEditorNote(initialValues.internalNote);
        setInternalNote(initialValues.internalNote);
        setHasUnsavedChanges(false);
        setShowUnsavedModal(false);
        resetAndClose();
        break;
      case 'save':
        // Save and close
        handleSave();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    shouldTrackChangesRef.current = false; // Stop tracking changes during init

    const calculatedContent =
      idSelected > 0
        ? initialContent.find((item) => item.contentid === idSelected)
        : {
            contenttypeid: 0,
            title: '',
            description: '',
            finaldescription: '',
            note: '',
            images: []
          };

    if (idSelected === 0 && contentTypeSelected) {
      setShouldShowDayElements(contentTypeSelected?.code === 'day');
      setContent({
        ...calculatedContent,
        contenttypeid: contentTypeSelected?.contenttypeid
      });
    } else {
      setContent(calculatedContent);
    }

    const normalizedDescription = normalizeValue(
      calculatedContent?.description
    );
    const normalizedFinalDescription = normalizeValue(
      calculatedContent?.finaldescription
    );
    const normalizedNote = normalizeValue(calculatedContent?.note);
    const normalizedTitle = normalizeValue(calculatedContent?.title);
    const normalizedImageUrl = (() => {
      if (
        calculatedContent?.images &&
        Array.isArray(calculatedContent.images) &&
        calculatedContent.images.length > 0
      ) {
        return getOldCoverImageUrl(
          calculatedContent.images[calculatedContent.images.length - 1].imageurl
        );
      }

      if (
        calculatedContent?.contentimage &&
        Array.isArray(calculatedContent.contentimage) &&
        calculatedContent.contentimage.length > 0
      ) {
        return getOldCoverImageUrl(
          calculatedContent.contentimage[
            calculatedContent.contentimage.length - 1
          ].imageurl
        );
      }
      return '';
    })();

    setMarkdown(normalizedDescription);
    setDescription(normalizedDescription);
    setEditorFinalDescription(normalizedFinalDescription);
    setFinalDescription(normalizedFinalDescription);
    setInternalNote(normalizedNote);
    setEditorNote(normalizedNote);
    setShowCreateFinalVersion(!normalizedFinalDescription);

    // Store initial values for comparison
    setInitialValues({
      title: normalizedTitle,
      description: normalizedDescription,
      finalDescription: normalizedFinalDescription,
      internalNote: normalizedNote,
      imageUrl: normalizedImageUrl
    });

    setHasUnsavedChanges(false);

    // Start tracking changes after everything is set up
    setTimeout(() => {
      shouldTrackChangesRef.current = true;
    }, 300);
  }, [idSelected, initialContent, contentTypeSelected]);

  useEffect(() => {
    const type = libraryContentTypes?.find(
      (item) => item?.contenttypeid == content?.contenttypeid
    );

    setShouldShowDayElements(type?.code === 'day');
  }, [content, libraryContentTypes]);

  // Track changes with proper comparison
  useEffect(() => {
    if (
      shouldTrackChangesRef.current &&
      openSlide &&
      initialValues &&
      Object.keys(initialValues).length > 0
    ) {
      const currentTitle = normalizeValue(content?.title);
      const currentDescription = normalizeValue(editorDescription);
      const currentFinalDescription = normalizeValue(editorFinalDescription);
      const currentInternalNote = normalizeValue(editorNote);
      const currentImageUrl = (() => {
        if (
          content?.images &&
          Array.isArray(content.images) &&
          content.images.length > 0
        ) {
          return getOldCoverImageUrl(
            content.images[content.images.length - 1].imageurl
          );
        }

        if (
          content?.contentimage &&
          Array.isArray(content.contentimage) &&
          content.contentimage.length > 0
        ) {
          return getOldCoverImageUrl(
            content.contentimage[content.contentimage.length - 1].imageurl
          );
        }
        return '';
      })();

      const hasChanges =
        currentTitle !== initialValues.title ||
        currentDescription !== initialValues.description ||
        currentFinalDescription !== initialValues.finalDescription ||
        currentInternalNote !== initialValues.internalNote ||
        currentImageUrl !== initialValues.imageUrl;

      setHasUnsavedChanges(hasChanges);
    }
  }, [
    content?.title,
    editorDescription,
    editorFinalDescription,
    editorNote,
    content?.images,
    content?.contentimage,
    initialValues,
    openSlide
  ]);

  // Reset tracking when slideout closes
  useEffect(() => {
    if (!openSlide) {
      shouldTrackChangesRef.current = false;
    }
  }, [openSlide]);

  // Handle accordion expansion changes
  const handleAccordionChange = (section) => (event, isExpanded) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: isExpanded
    }));
  };

  const handleCreateFinalVersion = () => {
    setShowCreateFinalVersion(false);
  };

  const [isImageUploading, setIsImageUploading] = useState(false);

  return (
    <Drawer
      hideBackdrop
      open={openSlide}
      onClose={handleClose}
      title={``}
      className={`library-item-slideout__drawer ${openSlide ? 'drawer-open' : ''}`}
      anchor={'right'}
      id='day-service-edit-slideout'
      position='right'
      {...rest}
    >
      <Box className='library-item-slideout'>
        {/* Fixed Header */}
        <Box className='library-item-slideout__header'>
          <Box className='library-item-slideout__header-title'>
            <Typography variant='h6' className='library-item-slideout__title'>
              {idSelected !== 0
                ? t('library:editContent')
                : t('library:newItem')}{' '}
              - {contentTypeSelected?.title}
            </Typography>
            <XGButton
              className='library-item-slideout__close-btn'
              type='tertiary'
              size='small'
              onClick={handleClose}
              startIcon={<X size={21} />}
              variant='text'
              id='detailed-view-edit-day-close-button'
              disabled={isSaving || isImageUploading}
            />
          </Box>
        </Box>

        {/* Scrollable Content */}
        <Box className='library-item-slideout__content'>
          <Box className='library-item-slideout__accordion'>
            <Typography
              variant='subtitle1'
              className='library-item-slideout__section-title'
            >
              {t('library:title')}*
            </Typography>
            <XGInput
              name='title'
              placeholder={t('library:enterTitle')}
              id='library-new-content-title'
              type='text'
              value={content?.title ? content.title : ''}
              onChange={handleChange}
              inputProps={{
                maxLength: 250
              }}
            />
          </Box>
          {/* Description Section */}
          <Accordion
            expanded={expandedSections?.description}
            onChange={handleAccordionChange('description')}
            className='library-item-slideout__accordion'
            disableGutters
            elevation={0}
          >
            {!showCreateFinalVersion && isDay ? (
              <AccordionSummary
                expandIcon={<CaretDown size={16} />}
                className='library-item-slideout__accordion-summary'
                sx={{
                  '& .MuiAccordionSummary-content': {
                    flexGrow: 0,
                    margin: 0
                  },
                  '& .MuiAccordionSummary-expandIconWrapper': {
                    marginLeft: '8px',
                    marginRight: 0
                  },
                  justifyContent: 'flex-start'
                }}
              >
                <Typography
                  variant='subtitle1'
                  className='library-item-slideout__section-title'
                >
                  {t('trip:description')}
                </Typography>
              </AccordionSummary>
            ) : (
              <Typography
                variant='subtitle1'
                className='library-item-slideout__section-title'
              >
                {t('trip:description')}
              </Typography>
            )}

            <AccordionDetails className='library-item-slideout__accordion-details'>
              <Editor
                editable={true}
                isPreviewing={false}
                content={description}
                id='day-overview-description-editor'
                setDescription={() => {}}
                setMarkdown={setMarkdown}
                setIsPreviewing={() => {}}
                showActions={false}
                placeholder={t('trip:enterDescription')}
                generatePrompt={
                  <AIGenerateButton
                    id='ai-generate-trip-description-button'
                    setMarkdown={setMarkdown}
                    setTripDescription={setDescription}
                    editorContent={editorDescription || description}
                    setIsEditorGenerating={setIsEditorGenerating}
                    disableGenerate={true}
                  />
                }
                isEditorDisabled={false}
                isEditorGenerating={isEditorGenerating}
              />
            </AccordionDetails>
          </Accordion>

          {showCreateFinalVersion && isDay && (
            <Box>
              <XGButton
                text={t('trip:createFinalVersion')}
                type='secondary'
                size='medium'
                variant='outlined'
                onClick={handleCreateFinalVersion}
                startIcon={<Plus size={20} />}
                className='library-item-slideout__create-final-btn'
                fullWidth
                id='create-final-version-button'
                //  disabled={isSubmitting}
              />
            </Box>
          )}

          {/* Final Description Section */}
          {!showCreateFinalVersion && isDay && (
            <>
              <Accordion
                expanded={expandedSections.finalDescription}
                onChange={handleAccordionChange('finalDescription')}
                className='library-item-slideout__accordion'
                disableGutters
                elevation={0}
              >
                <AccordionSummary
                  expandIcon={<CaretDown size={16} />}
                  className='library-item-slideout__accordion-summary'
                >
                  <Typography
                    variant='subtitle1'
                    className='library-item-slideout__section-title'
                  >
                    {t('trip:finalDescription')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className='library-item-slideout__accordion-details'>
                  <Editor
                    editable={true}
                    isPreviewing={false}
                    content={finalDescription}
                    id='day-overview-final-description-editor'
                    setDescription={() => {}}
                    setMarkdown={setFinalDescriptionMarkdown}
                    setIsPreviewing={() => {}}
                    showActions={false}
                    placeholder={t('trip:enterFinalDescription')}
                    generatePrompt={
                      <AIGenerateButton
                        id='ai-generate-trip-description-button'
                        setMarkdown={setFinalDescriptionMarkdown}
                        setTripDescription={setFinalDescription}
                        editorContent={
                          editorFinalDescription || finalDescription
                        }
                        setIsEditorGenerating={setIsFinalEditorGenerating}
                        disableGenerate={true}
                      />
                    }
                    isEditorGenerating={isFinalEditorGenerating}
                    isEditorDisabled={false}
                  />
                </AccordionDetails>
              </Accordion>
            </>
          )}

          {/* Image Section */}
          <Box className='library-item-slideout__section'>
            <Typography
              variant='subtitle1'
              className='library-item-slideout__section-title'
            >
              {t('common:image')}
            </Typography>

            <Box className='library-item-slideout__image-container'>
              <ImagePicker
                id='day-overview-image-picker'
                url={getCurrentImageUrl()}
                callback={insertImage}
                pathReference={
                  enableStockPhotoPicker
                    ? pathReference.LIBRARY
                    : pathReference.DAY_ITENERARY
                }
                fileType={fileType.IMAGE}
                showDelete={true}
                handleDelete={handleDeleteLibraryImage}
                disabledCTA={isImageUploading || isSaving}
                onUploadStart={() => setIsImageUploading(true)}
                onUploadComplete={() => setIsImageUploading(false)}
                onUploadError={() => setIsImageUploading(false)}
                className={'library-item-slideout__image-picker'}
              />
              <Box display={'flex'} flexDirection='column' gap={1}>
                <span className='image-picker__preview__info'>
                  {isDay
                    ? t('trip:tripDayImagePreviewInfo')
                    : t('trip:additionalContentImagePreviewInfo')}
                </span>
                <span className='image-picker__preview__note'>
                  {t('trip:tripImagePreviewNote', {
                    maxImageSize: bytesToMiB(globalConfig.maxImageSize)
                  })}
                </span>
              </Box>
            </Box>
          </Box>

          {/* Internal Notes Section */}
          {isDay && (
            <Accordion
              expanded={expandedSections.internalNotes}
              onChange={handleAccordionChange('internalNotes')}
              className='library-item-slideout__accordion'
              disableGutters
              elevation={0}
            >
              <AccordionSummary
                expandIcon={<CaretDown size={16} />}
                className='library-item-slideout__accordion-summary'
              >
                <Typography
                  variant='subtitle1'
                  className='library-item-slideout__section-title'
                >
                  Internal Notes
                </Typography>
              </AccordionSummary>
              <AccordionDetails className='library-item-slideout__accordion-details'>
                <Editor
                  editable={true}
                  isPreviewing={false}
                  content={internalNote}
                  id='day-overview-notes-editor'
                  setDescription={() => {}}
                  setMarkdown={setNoteMarkdown}
                  setIsPreviewing={() => {}}
                  showActions={false}
                  placeholder={t('trip:enterInternalNotes')}
                  generatePrompt={null}
                  isEditorDisabled={false}
                />
              </AccordionDetails>
            </Accordion>
          )}
        </Box>

        {/* Fixed Footer */}
        <Box className='library-item-slideout__footer'>
          <Box
            className='library-item-slideout__footer-actions'
            sx={{ justifyContent: isEditing ? 'space-between' : 'flex-end' }}
          >
            {idSelected && isEditing ? (
              <XGButton
                text={t('common:delete')}
                type='error'
                size='medium'
                variant='outlined'
                onClick={handleDeleteButton}
                startIcon={<Trash size={20} />}
                id='library-item-slideout-save-to-library-button'
                disabled={isSaving || isImageUploading}
              />
            ) : (
              <></>
            )}
            <Box className='library-item-slideout__primary-actions'>
              <XGButton
                text={t('common:cancel')}
                type='secondary'
                size='medium'
                variant='outlined'
                onClick={handleClose}
                id='library-item-slideout-cancel-button'
                disabled={isSaving || isImageUploading}
              />

              <Box className='relative-position'>
                <XGButton
                  text={t('common:saveChanges')}
                  type='primary'
                  size='medium'
                  variant='contained'
                  onClick={handleSave}
                  id='library-item-slideout-save-button'
                  disabled={isSaving || isImageUploading}
                />
                {isSaving && (
                  <CircularProgress
                    size={20}
                    className='spinner spinner__primary spinner--small'
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <UnsavedChangesModal
        isOpen={showUnsavedModal}
        onAction={handleUnsavedModalAction}
        isSubmitting={isSaving}
      />
    </Drawer>
  );
};

LibraryItemSlideOut.propTypes = {
  openSlide: PropTypes.bool.isRequired,
  handleSlideClose: PropTypes.func,
  libraryContentTypes: PropTypes.array.isRequired,
  handleOnItemAdded: PropTypes.func.isRequired,
  initialContent: PropTypes.array.isRequired,
  contentTypeSelected: PropTypes.object,
  isEditing: PropTypes.bool,
  idSelected: PropTypes.number,
  handleDelete: PropTypes.func
};

export default LibraryItemSlideOut;
